import { Component, EventEmitter, Inject, Input, OnChanges, Output, ViewChild } from '@angular/core';
import { AJSMessagesService } from 'src/admin-front/ajs-upgraded-providers';
import { AlcmeonContextService } from 'src/app/core/alcmeon-context/alcmeon-context.service';
import { SitesService } from 'src/app/mobile/shared/sites.service';
import { ActionButtonConfig } from 'src/app/shared/action-button-config';
import { CapabilityIdEnum } from 'src/app/shared/capabilities-and-permissions';
import { SelectModerationActionPopoverComponent } from 'src/app/shared/components/select-moderation-action-popover/select-moderation-action-popover.component';
import { SelectTagComponent } from 'src/app/shared/components/select-tags/select-tag/select-tag.component';
import { TagGroup, TaskStateInWsNotif } from 'src/app/shared/models/misc.model';
import { BasicService } from 'src/app/shared/services/basic.service';
import { ModalDismissRoleEnum } from 'src/app/task-process/answer-editor/send-and-popover/modal-dismiss-role.enum';
import { ArchiveAndOtherActionsService } from 'src/app/task-process/archive-and-other-actions/archive-and-other-actions.service';
import { TagsContext } from 'src/app/task-process/shared/tags-context.class';
import { Task } from 'src/app/tasks/shared/models';
import { CRMTypeEnum } from 'src/app/user-profile/shared/models';

import { Archive, AssigneeConstraints } from '../shared/models';
import { AssigneeListPopoverComponent } from './assignee-list-popover/assignee-list-popover.component';
import { TaskActionsService } from './task-actions.service';

@Component({
  selector: 'app-task-actions',
  templateUrl: './task-actions.component.html',
  styleUrls: ['./task-actions.component.scss'],
})
export class TaskActionsComponent implements OnChanges {
  @Input() task: Task;
  @Input() tagsContext: TagsContext;
  @Output() taskChange = new EventEmitter<Task>();
  @ViewChild('selectRoutingTag') selectRoutingTag: SelectTagComponent;

  buttonConfigs: ActionButtonConfig[] = [];
  routingTagGroup: TagGroup = null;

  constructor(
    private ajsMessagesService: AJSMessagesService,
    private alcmeonContextService: AlcmeonContextService,
    private archiveAndOtherActionsService: ArchiveAndOtherActionsService,
    private basicService: BasicService,
    private sitesService: SitesService,
    private taskActionsService: TaskActionsService,
    @Inject('$state') private $state: any
  ) {}

  ngOnChanges() {
    this.setupButtons();
    this.routingTagGroup = this.tagsContext.getRoutingTagGroup();
  }

  setupButtons() {
    this.buttonConfigs = [];

    if (this.task.is_assigned_to_team_of_logged_user) {
      // an archived deleted message keeps its 'deleted' state
      if (
        this.task.message.is_deleted &&
        this.task.state !== TaskStateInWsNotif.Archived &&
        this.task.state !== TaskStateInWsNotif.AutoArchived
      ) {
        this.setupProcessOrSeeButton(true);
        this.setupArchiveButton();
        this.setupRouteButton();
        this.setupAssignButton();
        this.setupModerateButton(true);
        this.setupCrmButton();
      } else {
        switch (this.task.state) {
          case TaskStateInWsNotif.Pending:
            this.setupProcessOrSeeButton();
            this.setupOthersButton();
            break;

          case TaskStateInWsNotif.OnHold:
            if (this.isAssignedToMe()) {
              this.setupProcessOrSeeButton();
              this.setupOthersButton();
            } else {
              this.setupSeeButton();
              this.setupOthersButton(true);
            }
            break;

          case TaskStateInWsNotif.Assigned:
            this.setupSeeButton();
            this.setupArchiveButton(!this.isAssignedToMe());
            this.setupRouteButton(!this.isAssignedToMe());
            this.setupAssignButton(true);
            this.setupModerateButton(!this.isAssignedToMe());
            this.setupCrmButton(!this.isAssignedToMe());
            break;

          case TaskStateInWsNotif.HelpRequested:
            if (
              this.alcmeonContextService.hasCapability(this.task.message.site.id, CapabilityIdEnum.ProcessHelpRequests)
            ) {
              this.setupProcessOrSeeButton();
              this.setupOthersButton();
            } else {
              this.setupSeeButton();
              this.setupOthersButton(true);
            }
            break;

          case TaskStateInWsNotif.HelpAnswered:
            // to my request
            if (this.isAssignedToMe()) {
              this.setupProcessOrSeeButton();
              this.setupOthersButton();
            }
            // to other request
            else {
              this.setupSeeButton();
              this.setupOthersButton(true);
            }
            break;

          case TaskStateInWsNotif.Answered:
          case TaskStateInWsNotif.Archived:
          case TaskStateInWsNotif.AutoArchived:
            this.setupSeeButton();
            this.setupOthersButton(true);
            break;
        }
      }
    }
    // routed to another team
    else {
      this.setupSeeButton(true);
      this.setupOthersButton(true);
    }
  }

  private isAssignedToMe() {
    return this.task.assigned_to.id === this.alcmeonContextService.self.id;
  }

  // ▼▼▼▼▼ setup buttons ▼▼▼▼▼

  private setupProcessOrSeeButton(isDisabled = false) {
    if (this.task.conversation?.is_assigned) {
      this.setupSeeButton(isDisabled);
    } else {
      this.setupProcessButton(isDisabled);
    }
  }

  private setupOthersButton(isDisabled = false) {
    this.setupArchiveButton(isDisabled);
    this.setupRouteButton(isDisabled);
    this.setupAssignButton(isDisabled);
    this.setupModerateButton(isDisabled);
    this.setupCrmButton(isDisabled);
  }

  private setupProcessButton(isDisabled = false) {
    const button = new ActionButtonConfig(
      this.basicService.translate('inbox/message/action/process'),
      'play-circle-outline',
      () => {
        this.process();
      }
    );
    button.isDisabledFn = () => isDisabled;
    button.dataTestId = 'process';
    this.buttonConfigs.push(button);
  }

  private setupSeeButton(isDisabled = false) {
    const button = new ActionButtonConfig(this.basicService.translate('inbox/message/action/view'), 'eye', () => {
      this.see();
    });
    button.isDisabledFn = () => isDisabled;
    button.dataTestId = 'see';
    this.buttonConfigs.push(button);
  }

  private setupArchiveButton(isDisabled = false) {
    const button = new ActionButtonConfig(
      this.basicService.translate('m/task-process/tooltips/archive'),
      'archive',
      () => this.archive()
    );
    button.isDisabledFn = () => isDisabled;
    this.buttonConfigs.push(button);
  }

  private setupRouteButton(isDisabled = false) {
    const button = new ActionButtonConfig(
      this.basicService.translate('m/task-process/tooltips/route'),
      'people',
      event => this.routeToTeam(event)
    );
    button.class = 'routing';
    button.isDisabledFn = () => isDisabled || !this.routingTagGroup;
    this.buttonConfigs.push(button);
  }

  private setupAssignButton(isDisabled = false) {
    if (this.alcmeonContextService.hasCapability(this.task.message.site.id, CapabilityIdEnum.Route)) {
      const button = new ActionButtonConfig(this.basicService.translate('inbox/assign/tooltip'), 'person', event =>
        this.getAssigneeList(event)
      );
      button.isDisabledFn = () => isDisabled;
      this.buttonConfigs.push(button);
    }
  }

  private setupModerateButton(isDisabled = false) {
    const button = new ActionButtonConfig(
      this.basicService.translate('m/task-process/tooltips/moderate'),
      null,
      event => {
        this.getModerationActions(event);
      }
    );
    const siteType = this.task.site.type_name;
    button.iconUrl = `assets/img/site_icon/${siteType}.svg`;

    const canHaveModerationActionFn = () =>
      isDisabled ||
      !this.archiveAndOtherActionsService.canHaveModerationAction(
        !this.task.message.is_private,
        this.task.site.type_name
      );
    button.isDisabledFn = canHaveModerationActionFn;
    this.buttonConfigs.push(button);
  }

  private setupCrmButton(isDisabled = false) {
    if (!this.task.message.is_from_self) {
      this.task.message.from.crm_organizations_links?.forEach(crm => {
        let button: ActionButtonConfig;

        let crmName: string;
        if (crm.type === CRMTypeEnum.salesforce) {
          crmName = 'salesforce';

          if (crm.user_is_linked) {
            button = new ActionButtonConfig(
              this.basicService.translate('inbox/message/action/crm-matched', { crm: crmName }),
              null,
              () => {
                this.showCrmInUserInteractions(crm.crm_organization_id);
              }
            );
          } else {
            button = new ActionButtonConfig(
              this.basicService.translate('inbox/message/action/crm-unmatched'),
              null,
              () => {
                this.showCrmInUserInteractions(crm.crm_organization_id);
              }
            );
            button.class = 'unmatched';
          }

          button.iconUrl = `assets/img/${crmName}-logo.svg`;
        } else if (crm.type === CRMTypeEnum.zendesk) {
          crmName = 'zendesk';
          if (crm.user_is_linked) {
            button = new ActionButtonConfig(
              this.basicService.translate('inbox/message/action/crm-matched', { crm: crmName }),
              null,
              () => {
                this.showCrmInUserInteractions(crm.crm_organization_id);
              }
            );
          } else {
            button = new ActionButtonConfig(
              this.basicService.translate('inbox/message/action/crm-unmatched'),
              null,
              () => {
                this.showCrmInUserInteractions(crm.crm_organization_id);
              }
            );
            button.class = 'unmatched';
          }

          button.iconUrl = `assets/img/${crmName}-logo.svg`;
        }

        if (button) {
          button.isDisabledFn = () => isDisabled;
          this.buttonConfigs.push(button);
        }
      });
    }
  }

  // ▼▼▼▼▼ actions ▼▼▼▼▼

  private process() {
    this.taskActionsService.assign(this.task.id, this.task.state).subscribe({
      next: response => {
        if (response.tasks_by_id[this.task.id]) {
          this.see();
          this.task = response.tasks_by_id[this.task.id];
          this.refreshTask();
        }
      },
      error: response => {
        if (response.status !== -1) {
          if (response.error.reason === 'no-rights') {
            this.basicService.toastAlert('no-rights');
          } else {
            this.basicService.toastAlert('task/actions/error/assign');
          }
        }
      },
    });
  }

  private see(taskId?: number) {
    if (this.$state.includes('**.process') || this.$state.is('auth.alcmeon.pushbox.tasks')) {
      this.$state.go('^.process', { processedTaskId: taskId ?? this.task.id });
    } else {
      this.$state.go('.process', { processedTaskId: taskId ?? this.task.id });
    }
  }

  private archive() {
    const data: Archive = {
      acquire: this.task.state === TaskStateInWsNotif.Pending,
      from_on_hold: this.task.state === TaskStateInWsNotif.OnHold,
    };
    this.taskActionsService.archive(this.task.id, data).subscribe({
      next: result => {
        if (result.tasks_by_id[this.task.id]) {
          this.basicService.toastSuccess('task/actions/info/archive');
          this.task = result.tasks_by_id[this.task.id];
          this.refreshTask();
        }
      },
      error: () => {
        this.basicService.toastAlert('task/actions/error/archive');
      },
    });
  }

  private routeToTeam(event: Event) {
    this.selectRoutingTag.open(event);
  }

  private getAssigneeList(event: Event) {
    // we make sure that whoever is picked satisfies at least this condition
    const constraints: AssigneeConstraints[] = [
      {
        site_id: this.task.site.id,
        type: this.task.type,
        routing_tag_id: null,
      },
    ];

    // then, any extra constraint introduced by the routing tags (there could be none)
    const routingTagIndex = this.task.tags.findIndex(tag => tag.group.id === this.routingTagGroup.id);

    if (routingTagIndex > -1) {
      constraints.push({
        site_id: this.task.site.id,
        type: this.task.type,
        routing_tag_id: this.task.tags[routingTagIndex].id,
      });
    }

    this.taskActionsService.getAssigneeList(constraints).subscribe({
      next: async assigneeList => {
        const modalResult = await this.basicService.popoverWithResult(
          event,
          AssigneeListPopoverComponent,
          {
            assigneeList,
          },
          'alc-select-tag-in-popover'
        );

        if (modalResult.role === ModalDismissRoleEnum.OK) {
          this.assignTo(modalResult.data);
        }
      },
      error: response => {
        if (response.status !== -1) {
          this.basicService.toastAlert('task/actions/error/gathering_assignee_users');
        }
      },
    });
  }

  private assignTo(userId: number) {
    const data = {
      tasks_ids: [this.task.id],
    };

    this.taskActionsService.assignTo(userId, data).subscribe({
      next: result => {
        if (result.tasks_by_id[this.task.id]) {
          if (userId === this.alcmeonContextService.self.id) {
            this.see(this.task.id);
          }
          this.basicService.toastSuccess('task/actions/info/assign');
          this.task = result.tasks_by_id[this.task.id];
          this.refreshTask();
        }
      },
      error: () => {
        this.basicService.toastAlert('task/actions/error/assign');
      },
    });
  }

  private async getModerationActions(event: Event) {
    const componentProps = {
      messageId: this.task.message.id,
      siteId: this.task.message.site.id,
    };

    const modalResult = await this.basicService.popoverWithResult(
      event,
      SelectModerationActionPopoverComponent,
      componentProps
    );
    if (modalResult.role === ModalDismissRoleEnum.OK) {
      this.moderate(modalResult.data.actionType);
    }
  }

  private moderate(actionType: string) {
    const messageId = this.task.message.id;
    const siteId = this.task.message.site.id;
    const taskId = this.task.id;
    this.archiveAndOtherActionsService.performModerationAction(siteId, taskId, messageId, actionType).subscribe({
      next: ({ message }) => {
        this.basicService.toastSuccess('m/task-process/moderation-action/sucess');
        this.task.message = message;
        this.refreshTask();
      },
    });
  }

  private showCrmInUserInteractions(crmOrganizationId: number) {
    const companyId = this.alcmeonContextService.companyId;
    const siteId = this.task.message.site.id;
    const userId = this.task.message.from.id;
    const avatarUrl = this.task.message.from.details.avatarUrl;
    const taskId = this.task.id;
    this.ajsMessagesService.sites = this.sitesService.sitesV2;
    this.ajsMessagesService.showUserInteractions(companyId, siteId, userId, avatarUrl, taskId, crmOrganizationId);
  }

  private refreshTask() {
    this.setupButtons();
    this.taskChange.emit(this.task);
  }
}
